// Media library: images this team already uses or uploaded, to reuse instead of uploading again
import { createContext, useContext, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { supabase } from "@/lib/agents/db"
import { cn } from "@/lib/utils"
import { Images, Search } from "lucide-react"
import { SMART_CMS_BUCKET, uploadFolder } from "../uploadImage"

export interface MediaItem {
  url: string
  // Where it's used, e.g. "Header image · Legal aid in Athens"
  usedIn: string[]
}

// The page hands over its stores; the library scans them when it opens
export const MediaSourcesContext = createContext<() => { label: string; records: Record<string, any> }[]>(() => [])

const IMAGE_URL = /^(https?:\/\/[^\s"'<>]+?\.(?:png|jpe?g|webp|gif|svg|avif)(?:\?[^\s"'<>]*)?|https?:\/\/[^\s"'<>]+\/storage\/v1\/object\/public\/[^\s"'<>]+)$/i
const IMG_SRC = /<img[^>]+src=["']([^"']+)["']/gi

// Every image URL in a record: image fields, and pictures inside HTML
function collect(value: unknown, add: (url: string) => void, depth = 0) {
  if (depth > 6 || value == null) return
  if (typeof value === "string") {
    if (IMAGE_URL.test(value.trim())) add(value.trim())
    else if (value.includes("<img")) for (const m of value.matchAll(IMG_SRC)) add(m[1])
  } else if (Array.isArray(value)) value.forEach(v => collect(v, add, depth + 1))
  else if (typeof value === "object") Object.values(value as object).forEach(v => collect(v, add, depth + 1))
}

const titleOf = (r: any) =>
  r?.title || r?.name || Object.values(r?.translations ?? {}).map((t: any) => t?.title || t?.name).find(Boolean) || "Untitled"

export function scanMedia(sources: { label: string; records: Record<string, any> }[]): MediaItem[] {
  const byUrl = new Map<string, Set<string>>()
  for (const { label, records } of sources) {
    for (const r of Object.values(records ?? {})) {
      collect(r, url => {
        if (!byUrl.has(url)) byUrl.set(url, new Set())
        byUrl.get(url)!.add(`${label} · ${titleOf(r)}`)
      })
    }
  }
  return [...byUrl].map(([url, used]) => ({ url, usedIn: [...used] }))
}

// This team's own uploads; older uploads aren't in a team folder and only show where they're used
export async function teamUploads(): Promise<string[]> {
  const folder = uploadFolder()
  if (!folder) return []
  const { data, error } = await supabase.storage.from(SMART_CMS_BUCKET).list(folder, { limit: 500, sortBy: { column: "created_at", order: "desc" } })
  if (error || !data) return []
  return data
    .filter(f => f.name && !f.name.endsWith("/"))
    .map(f => supabase.storage.from(SMART_CMS_BUCKET).getPublicUrl(`${folder}/${f.name}`).data.publicUrl)
}

export function MediaLibraryButton({ onPick, className, icon, onOpen, disabled }: {
  onPick: (url: string) => void
  className?: string
  // A toolbar icon instead of a labelled button
  icon?: boolean
  // Runs before the dialog takes focus, e.g. to remember the caret
  onOpen?: () => void
  disabled?: boolean
}) {
  const sources = useContext(MediaSourcesContext)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [uploads, setUploads] = useState<string[]>([])
  const [broken, setBroken] = useState<Set<string>>(new Set())

  const items = useMemo(() => {
    if (!open) return []
    const used = scanMedia(sources())
    const known = new Set(used.map(i => i.url))
    return [...used, ...uploads.filter(u => !known.has(u)).map(url => ({ url, usedIn: [] }))]
  }, [open, sources, uploads])

  const q = query.trim().toLowerCase()
  const shown = items.filter(i => !broken.has(i.url) && (!q || i.url.toLowerCase().includes(q) || i.usedIn.some(u => u.toLowerCase().includes(q))))

  return (
    <>
      <Button
        type="button" size={icon ? "icon" : "sm"} variant={icon ? "ghost" : "outline"}
        className={cn(icon ? "h-7 w-7" : "h-8 shrink-0 gap-1 text-xs", className)}
        title="Choose from the media library" aria-label="Choose from the media library"
        disabled={disabled}
        onClick={() => { onOpen?.(); setOpen(true); void teamUploads().then(setUploads) }}
      >
        <Images className="h-3.5 w-3.5" />{!icon && " Library"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[80vh] max-w-3xl flex-col gap-3">
          <DialogHeader>
            <DialogTitle>Media library</DialogTitle>
            <DialogDescription>Images this team already uses or uploaded. Picking one reuses it, without uploading again.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by where it's used or its file name" className="h-8 pl-7 text-xs" />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {shown.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">{items.length ? "No images match." : "No images yet. Uploaded images appear here."}</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {shown.map(i => (
                  <button
                    key={i.url}
                    type="button"
                    onClick={() => { onPick(i.url); setOpen(false) }}
                    className="group overflow-hidden rounded-md border text-left hover:border-primary"
                    title={i.usedIn.join("\n") || "Uploaded, not used yet"}
                  >
                    <div className="flex aspect-video items-center justify-center bg-muted/40">
                      <img
                        src={i.url} alt="" loading="lazy"
                        className="max-h-full max-w-full object-contain"
                        onError={() => setBroken(prev => new Set(prev).add(i.url))}
                      />
                    </div>
                    <p className="truncate px-1.5 py-1 text-[10px] text-muted-foreground">
                      {i.usedIn.length ? `${i.usedIn[0]}${i.usedIn.length > 1 ? ` +${i.usedIn.length - 1}` : ""}` : "Not used yet"}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
